import { Component, ElementRef, inject, ViewChild, signal, computed, ChangeDetectionStrategy } from '@angular/core';

import { MatDialogRef } from '@angular/material/dialog';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { FormContainerComponent } from '@olv/ui-shared/ui-shared/form-container/form-container.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { FormErrorService } from '@olv/ui-shared/services/form-error.service';
import { ValidationService } from '@olv/ui-shared/services/validation.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';

@Component({
  selector: 'app-account-photo',
  standalone: true,
  imports: [
    ImageCropperComponent,
    FormContainerComponent,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    ButtonComponent,
  ],
  templateUrl: './account-photo.component.html',
  styleUrls: ['./account-photo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountPhotoComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private dialogRef = inject(MatDialogRef<AccountPhotoComponent>);
  private formErrorService = inject(FormErrorService);

  readonly attachedPhoto = new FormControl<File | null>(null, {
    validators: [ValidationService.fileTypeValidator(['.jpg', '.jpeg', '.png']), ValidationService.fileSizeValidator()],
    nonNullable: false,
  });

  imageFile = signal<File | null>(null);
  croppedBlob = signal<Blob | null>(null);
  scale = signal<number>(1);
  rotation = signal<number>(0);
  isDragging = signal<boolean>(false);

  readonly ZOOM_CONFIG = {
    min: 1,
    max: 3,
    sliderStep: 0.01,
    buttonStep: 0.1,
  };
  readonly ROTATION_STEP = 90;

  transform = computed<ImageTransform>(() => ({
    scale: this.scale(),
    rotate: this.rotation(),
  }));

  zoomPercent = computed(() => {
    return ((this.scale() - this.ZOOM_CONFIG.min) / (this.ZOOM_CONFIG.max - this.ZOOM_CONFIG.min)) * 100;
  });

  getFieldError(): string {
    return this.formErrorService.getErrorMessage(this.attachedPhoto, 'Photo');
  }

  get saveButtonState(): 'disabled' | 'default' {
    return this.getFieldError() || !this.imageFile() || !this.croppedBlob() ? 'disabled' : 'default';
  }

  onUpload(): void {
    this.fileInput?.nativeElement.click();
  }

  fileChangeEvent(event: Event): void {
    const photoInput = event.target as HTMLInputElement;
    const file = photoInput.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }

  private handleFile(file: File): void {
    this.attachedPhoto.setValue(file);
    this.attachedPhoto.markAsTouched();
    this.attachedPhoto.updateValueAndValidity();

    if (this.attachedPhoto.valid) {
      this.imageFile.set(file);
      this.resetControls();
    } else {
      this.resetImageState();
    }

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  handleImageCropped(event: ImageCroppedEvent): void {
    this.croppedBlob.set(event.blob || null);
  }

  saveCroppedImage(): void {
    const blob = this.croppedBlob();
    if (!blob) return;

    const extension = blob.type.split('/')[1];
    const fileName = `profile-photo.${extension}`;
    const file = new File([blob], fileName, { type: blob.type });

    this.dialogRef.close(file);
  }

  onClose(): void {
    this.dialogRef.close();
  }

  private resetImageState(): void {
    this.imageFile.set(null);
    this.croppedBlob.set(null);
    this.resetControls();
  }

  onResetState(): void {
    this.resetImageState();
    this.attachedPhoto.reset();
  }

  private resetControls(): void {
    this.scale.set(1);
    this.rotation.set(0);
  }

  onZoomIn(): void {
    this.scale.update((s) => Math.min(s + this.ZOOM_CONFIG.buttonStep, this.ZOOM_CONFIG.max));
  }

  onZoomOut(): void {
    this.scale.update((s) => Math.max(s - this.ZOOM_CONFIG.buttonStep, this.ZOOM_CONFIG.min));
  }

  onZoomChange(value: Event): void {
    this.scale.set((value.target as HTMLInputElement).valueAsNumber);
  }

  onRotate(): void {
    this.rotation.update((r) => (r + this.ROTATION_STEP) % 360);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFile(file);
    }
  }
}
